import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { RiArrowDropDownFill } from 'react-icons/ri';
import { TbUsers } from 'react-icons/tb';

const MORE_VALUE = 'more';

const GuestInput = ({ guest, setGuest, isLoading, IS_FORNO }) => {
  const theme = useTheme();
  const inputRef = useRef(null);

  const maxPreset = IS_FORNO ? 8 : 10;
  const presetOptions = Array.from({ length: maxPreset }, (_, i) => i + 1);

  const parsedGuest = parseInt(guest, 10);

  const isPresetGuest =
    !Number.isNaN(parsedGuest) && parsedGuest >= 1 && parsedGuest <= maxPreset;

  const [isCustom, setIsCustom] = useState(
    () =>
      guest !== '' && guest !== null && guest !== undefined && !isPresetGuest,
  );

  // Local value used while the user is typing
  const [customGuest, setCustomGuest] = useState(
    guest === null || guest === undefined ? '' : String(guest),
  );

  /*
   * Keep the local input in sync when the guest value
   * changes externally, for example when a preset is selected.
   */
  useEffect(() => {
    if (!isCustom) {
      setCustomGuest(
        guest === null || guest === undefined ? '' : String(guest),
      );
    }
  }, [guest, isCustom]);

  /*
   * Focus the custom input when "More" is selected
   */
  useEffect(() => {
    if (isCustom && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select?.();
    }
  }, [isCustom]);

  /*
   * Update only the local input value while typing.
   *
   * This prevents the parent guest state from changing on
   * every keystroke and triggering an API request immediately.
   */
  const handleCustomChange = (event) => {
    const value = event.target.value;

    setCustomGuest(value);
  };

  /*
   * Update the actual guest value only after the user
   * finishes typing and leaves the input.
   */
  const handleCustomBlur = () => {
    if (customGuest === '') {
      setGuest(1);
      setCustomGuest('1');
      setIsCustom(false);
      return;
    }

    let next = parseInt(customGuest, 10);

    if (Number.isNaN(next) || next < 1) {
      next = 1;
    }

    if (IS_FORNO && next > 8) {
      next = 8;
    }

    setGuest(next);
    setCustomGuest(String(next));

    /*
     * If the entered number is within the preset range,
     * return to the dropdown view.
     */
    if (next >= 1 && next <= maxPreset) {
      setIsCustom(false);
    }
  };

  /*
   * Handle selecting a preset or "More"
   */
  const handlePresetChange = (event) => {
    const value = event.target.value;

    if (value === MORE_VALUE) {
      setIsCustom(true);
      setCustomGuest('');
      return;
    }

    const numericValue = Number(value);

    setIsCustom(false);
    setGuest(numericValue);
    setCustomGuest(String(numericValue));
  };

  const displayGuest = isPresetGuest ? parsedGuest : guest;

  const guestLabel =
    displayGuest === 1 || displayGuest === '1'
      ? '1 Guest'
      : displayGuest
        ? `${displayGuest} Guests`
        : 'Guests';

  const fieldSx = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    border: '1px solid',
    borderColor: 'divider',
    px: 2,
    mt: 1.5,
    boxSizing: 'border-box',
    borderRadius: '8px',
    height: '44px',
    bgcolor: 'background.paper',
  };

  return (
    <Box>
      <InputLabel
        sx={{
          fontSize: '12px',
          fontWeight: 800,
          color: 'text.secondary',
          display: 'flex',
          alignItems: 'center',
          gap: 0.75,
        }}
      >
        <TbUsers />
        Number of Guests
      </InputLabel>

      {isCustom ? (
        <Box sx={fieldSx}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              flex: 1,
              minWidth: 0,
            }}
          >
            <TbUsers
              style={{
                color: theme.palette.text.secondary,
                flexShrink: 0,
              }}
            />

            <TextField
              inputRef={inputRef}
              type="number"
              inputMode="numeric"
              value={customGuest}
              disabled={isLoading}
              onChange={handleCustomChange}
              onBlur={handleCustomBlur}
              placeholder="Enter guest count"
              variant="standard"
              InputProps={{
                disableUnderline: true,
              }}
              inputProps={{
                min: 1,
                max: IS_FORNO ? 8 : undefined,
                style: {
                  fontSize: '12px',
                  fontWeight: 500,
                  padding: 0,
                  color: theme.palette.text.primary,
                },
              }}
              sx={{
                flex: 1,
                '& .MuiInputBase-root': {
                  fontSize: '12px',
                },
              }}
            />
          </Box>
        </Box>
      ) : (
        <Select
          value={isPresetGuest ? String(parsedGuest) : ''}
          onChange={handlePresetChange}
          disabled={isLoading}
          displayEmpty
          IconComponent={() => (
            <RiArrowDropDownFill
              style={{
                fontSize: '20px',
                color: theme.palette.text.secondary,
              }}
            />
          )}
          renderValue={() => (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <TbUsers
                style={{
                  color: theme.palette.text.secondary,
                }}
              />

              <Typography
                sx={{
                  fontWeight: 500,
                  fontSize: '12px',
                  color: 'text.primary',
                }}
              >
                {guestLabel}
              </Typography>
            </Box>
          )}
          sx={{
            ...fieldSx,
            cursor: 'pointer',

            '& .MuiSelect-select': {
              display: 'flex',
              alignItems: 'center',
              py: 0,
              px: 0,
              minHeight: 'unset',
            },

            '& .MuiOutlinedInput-notchedOutline': {
              border: 'none',
            },

            '& fieldset': {
              border: 'none',
            },
          }}
        >
          {presetOptions.map((count) => (
            <MenuItem key={count} value={String(count)}>
              {count} Guest{count === 1 ? '' : 's'}
            </MenuItem>
          ))}

          <MenuItem value={MORE_VALUE}>More</MenuItem>
        </Select>
      )}

      {!guest && guest !== 0 && (
        <Typography
          sx={{
            color: 'error.main',
            fontSize: '11px',
            mt: 1,
          }}
        >
          Please enter a valid guest size
        </Typography>
      )}
    </Box>
  );
};

export default GuestInput;
